/* global React */
/* ──────────────────────────────────────────────────────────────────────
   BIM Journey — interactive isometric city (home page hero)
   ──────────────────────────────────────────────────────────────────────
   Ported from the Design Zone Shopify theme section
   `sections/bim-journey.liquid` (theme "Design Zone — BIM (no F1)").
   The markup, the drawing script and the stylesheet (css/bim-journey.css)
   are the original section's, with the Liquid settings replaced by the
   site's content files:

     · copy, buttons, Stage / Play / Pause / Restart → window.DZ_HOME.hero
     · city labels                                  → window.DZ_HOME.hero.city
     · stages + their three services                → window.DZ_SERVICES.stages

   Left: headline and two buttons. Right: an isometric night city where
   every stage is a building on its own plot. Hovering a building raises
   it; clicking it flies inside, where the stage's three services are the
   building's three floors. Back (or Esc) returns to the city. Bottom: a
   numbered stepper that plays through the stages and lights each
   district in turn. On narrower screens the map pans to the current
   district.

   The script owns this DOM (it draws into the SVGs and, on phones, moves
   the interior panel to <body>), so React renders an empty wrapper once
   and the markup is written into it before the first paint.
   ────────────────────────────────────────────────────────────────────── */

const { useLayoutEffect: useBjLayoutEffect, useRef: useBjRef } = React;

function bjEsc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// JSON inside <script type="application/json">: keep "</script>" out.
function bjJson(v) { return JSON.stringify(v).replace(/</g, '\\u003c'); }
function bjPad(n) { return (n < 10 ? '0' : '') + n; }

const BJ_ARROW = '<path d="M5 12h13m-5-6 6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>';

function bjMarkup(hero, stages) {
  const c = hero.city || {};
  const lc = hero.lifecycle || {};
  const sid = 'home';
  const total = stages.length;
  const first = stages[0] || { title: '' };
  const city = stages.map((s) => ({ title: s.title, services: s.services }));
  const steps = stages.map((s, i) =>
    '<li><button type="button" class="dz__step" data-title="' + bjEsc(s.title) + '"' + (i === 0 ? ' aria-current="step"' : '') + '>' +
      '<span class="dz__step-num">' + bjPad(i + 1) + '</span>' +
      '<span class="dz__step-label">' + bjEsc(s.title) + '</span>' +
      '<span class="dz__step-bar" aria-hidden="true"><span></span></span>' +
    '</button></li>'
  ).join('');

  return (
'<div class="dz dz--' + sid + '" id="dz-' + sid + '" data-interval="' + ((c.intervalSec || 6) * 1000) + '" data-enter-label="' + bjEsc(c.enterLabel) + '"' +
  (total > 1 ? ' role="region" aria-roledescription="carousel" aria-label="' + bjEsc(c.stepperLabel) + '"' : '') + '>' +
  '<div class="dz__inner">' +
    '<div class="dz__copy">' +
      (hero.eyebrow ? '<p class="dz__kicker"><span class="dz__kicker-dot" aria-hidden="true"></span>' + bjEsc(hero.eyebrow) + '</p>' : '') +
      '<h1 class="dz__heading" id="hero-title">' +
        bjEsc(hero.titleLeading) +
        (hero.titleAccent ? ' <span class="dz__highlight">' + bjEsc(hero.titleAccent) + '</span>' : '') +
        bjEsc(hero.titlePunct != null ? hero.titlePunct : ',') +
        (hero.titleTrailing ? '<span class="dz__after">' + bjEsc(hero.titleTrailing) + '</span>' : '') +
      '</h1>' +
      (hero.lead ? '<p class="dz__sub">' + bjEsc(hero.lead) + '</p>' : '') +
      '<div class="dz__actions">' +
        (hero.ctas && hero.ctas.primary ? '<a class="dz__btn dz__btn--primary" href="' + bjEsc(hero.ctas.primary.href) + '">' + bjEsc(hero.ctas.primary.label) +
          '<svg class="dz__btn-arrow" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' + BJ_ARROW + '</svg></a>' : '') +
        (hero.ctas && hero.ctas.secondary ? '<a class="dz__btn dz__btn--ghost" href="' + bjEsc(hero.ctas.secondary.href) + '">' + bjEsc(hero.ctas.secondary.label) + '</a>' : '') +
      '</div>' +
    '</div>' +

    '<div class="dz__scene">' +
      '<div class="dz__map">' +
        '<div class="dz-drift">' +
          '<div class="dz-cam">' +
            '<svg class="dz-sim" role="group" aria-label="' + bjEsc(c.stepperLabel) + '" focusable="false">' +
              '<defs>' +
                '<pattern id="dz-wl-' + sid + '" width="20" height="10" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 0.5 0 1 0 0)">' +
                  '<rect class="dz-win" x="2" y="3" width="5" height="4.5"/><rect class="dz-win dz-win--dim" x="12" y="3" width="5" height="4.5"/>' +
                '</pattern>' +
                '<pattern id="dz-wr-' + sid + '" width="20" height="10" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 -0.5 0 1 0 0)">' +
                  '<rect class="dz-win dz-win--dim" x="2" y="3" width="5" height="4.5"/><rect class="dz-win" x="12" y="3" width="5" height="4.5"/>' +
                '</pattern>' +
                '<pattern id="dz-wll-' + sid + '" width="20" height="10" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 0.5 0 1 0 0)">' +
                  '<rect class="dz-win dz-win--lit" x="2" y="3" width="5" height="4.5"/><rect class="dz-win dz-win--lit" x="12" y="3" width="5" height="4.5"/>' +
                '</pattern>' +
                '<pattern id="dz-wrl-' + sid + '" width="20" height="10" patternUnits="userSpaceOnUse" patternTransform="matrix(0.866 -0.5 0 1 0 0)">' +
                  '<rect class="dz-win dz-win--lit" x="2" y="3" width="5" height="4.5"/><rect class="dz-win dz-win--lit" x="12" y="3" width="5" height="4.5"/>' +
                '</pattern>' +
                '<linearGradient id="dz-heat-' + sid + '" x1="0" y1="0" x2="0" y2="1">' +
                  '<stop offset="0" stop-color="#e64a33"/><stop offset="0.45" stop-color="#eea33a"/><stop offset="1" stop-color="#3fb27f"/>' +
                '</linearGradient>' +
                '<radialGradient id="dz-glow-' + sid + '"><stop offset="0" stop-color="#ffd98a" stop-opacity="0.55"/><stop offset="1" stop-color="#ffd98a" stop-opacity="0"/></radialGradient>' +
              '</defs>' +
            '</svg>' +
            '<svg class="dz-traffic" aria-hidden="true" focusable="false"></svg>' +
            '<div class="dz-veil" aria-hidden="true"></div>' +
            '<svg class="dz-top" aria-hidden="true" focusable="false"></svg>' +
          '</div>' +
        '</div>' +
        '<p class="dz-hint" aria-hidden="true">' +
          '<span class="dz-hint__dot"></span>' +
          '<span class="dz-hint__text" data-dz-hint data-mouse="' + bjEsc(c.mapHint) + '" data-touch="' + bjEsc(c.mapHintTouch) + '">' + bjEsc(c.mapHint) + '</span>' +
          '<span class="dz-hint__progress" data-dz-progress data-text="' + bjEsc(c.exploredLabel) + '"></span>' +
        '</p>' +
        '<div class="dz-card" data-dz-card hidden>' +
          '<div class="dz-card__head">' +
            '<p class="dz-card__meta"><span class="dz-card__num" data-dz-card-num></span><span data-dz-card-meta></span></p>' +
            '<button type="button" class="dz-card__close" data-dz-card-close aria-label="' + bjEsc(c.closeLabel) + '">' +
              '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>' +
            '</button>' +
          '</div>' +
          '<p class="dz-card__title" data-dz-card-title></p>' +
          '<button type="button" class="dz-card__enter" data-dz-card-enter>' + bjEsc(c.enterButtonLabel) +
            '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">' + BJ_ARROW + '</svg>' +
          '</button>' +
        '</div>' +
        '<div class="dz-inside dz-inside--' + sid + '" role="region" aria-label="' + bjEsc(c.stepperLabel) + '" hidden>' +
          '<div class="dz-inside__bar">' +
            '<button type="button" class="dz-inside__back" data-dz-back>' +
              '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M19 12H6m5-6-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              bjEsc(c.backLabel) +
            '</button>' +
            '<div class="dz-inside__hop">' +
              '<button type="button" class="dz-inside__hop-btn" data-dz-hop="-1" aria-label="' + bjEsc(c.prevBuildingLabel) + '">' +
                '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
              '<button type="button" class="dz-inside__hop-btn" data-dz-hop="1" aria-label="' + bjEsc(c.nextBuildingLabel) + '">' +
                '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
          '<p class="dz-inside__meta"><span class="dz-inside__num" data-dz-room-num></span><span data-dz-room-meta></span></p>' +
          '<h2 class="dz-inside__title" tabindex="-1" data-dz-room-title></h2>' +
          '<div class="dz-inside__tower">' +
            '<div class="dz-inside__lift" aria-hidden="true"><span></span></div>' +
            '<div class="dz-inside__floors" data-dz-floors></div>' +
          '</div>' +
          '<div class="dz-inside__swipe" aria-hidden="true" data-dz-dots></div>' +
          '<template data-dz-floor-tpl>' +
            '<div class="dz-floor">' +
              '<div class="dz-floor__room" aria-hidden="true">' +
                '<svg class="dz-floor__art" viewBox="0 0 240 120" preserveAspectRatio="xMidYMid slice" focusable="false"></svg>' +
              '</div>' +
              '<div class="dz-floor__info">' +
                '<span class="dz-floor__lot"></span>' +
                '<span class="dz-floor__name"></span>' +
                '<a class="dz-floor__go" href="#">' + bjEsc(c.exploreLabel) +
                  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">' + BJ_ARROW + '</svg>' +
                '</a>' +
              '</div>' +
            '</div>' +
          '</template>' +
        '</div>' +
      '</div>' +
      '<script type="application/json" data-dz-labels>' + bjJson({ services: c.servicesLabel || '' }) + '</script>' +
      '<script type="application/json" data-dz-city>' + bjJson(city) + '</script>' +
    '</div>' +
  '</div>' +

  (total > 0 ?
  '<div class="dz__stepper">' +
    '<div class="dz__stepper-head">' +
      '<div>' +
        '<p class="dz__stepper-count">' + bjEsc(lc.stagePrefix) + ' <span data-dz-index>01</span> / ' + bjPad(total) + '</p>' +
        '<p class="dz__stepper-title" data-dz-title>' + bjEsc(first.title) + '</p>' +
      '</div>' +
      (total > 1 ?
      '<div class="dz__controls">' +
        '<button type="button" class="dz__ctl dz__ctl--prev" aria-label="' + bjEsc(c.prevLabel) + '">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button type="button" class="dz__ctl dz__ctl--restart" aria-label="' + bjEsc(lc.restartLabel) + '">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button type="button" class="dz__ctl dz__ctl--play" aria-label="' + bjEsc(lc.pauseLabel) + '" aria-pressed="false" data-pause="' + bjEsc(lc.pauseLabel) + '" data-play="' + bjEsc(lc.playLabel) + '">' +
          '<svg class="dz__icon-pause" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 6v12M15 6v12" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>' +
          '<svg class="dz__icon-play" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor"/></svg>' +
        '</button>' +
        '<button type="button" class="dz__ctl dz__ctl--next" aria-label="' + bjEsc(c.nextLabel) + '">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
      '</div>' : '') +
    '</div>' +
    '<ol class="dz__steps" role="list" style="--dz-count: ' + total + ';">' + steps + '</ol>' +
  '</div>' : '') +
  '<p class="visually-hidden" aria-live="polite" data-dz-live></p>' +
'</div>'
  );
}

/* Stages from the services catalogue, each with its three service links
   (the same /services/<stage>/#service-<service> anchors the Shopify
   section linked to). */
function bjStages() {
  const ar = window.location.pathname.startsWith('/ar/') ? '/ar' : '';
  const list = (window.DZ_SERVICES && window.DZ_SERVICES.stages) || [];
  return list.map((s) => ({
    title: s.label,
    services: (s.services || []).slice(0, 3).map((v) => ({
      name: v.name,
      url: ar + '/services/' + s.slug + '/#service-' + v.slug
    }))
  }));
}

// ── Original section script (sections/bim-journey.liquid) ──
// Unchanged apart from: `root` is passed in instead of looked up by the
// Shopify section id, "{{ sid }}" is "home", and the theme-editor
// (shopify:block:select) listener is dropped.
function bjInit(root) {
  if (!root || root.dataset.dzReady) return;
  root.dataset.dzReady = '1';

  var steps = root.querySelectorAll('.dz__step');
  var stepItems = root.querySelectorAll('.dz__steps > li');
  var map = root.querySelector('.dz__map');
  var svg = root.querySelector('.dz-sim');
  var cam = root.querySelector('.dz-cam');
  var drifter = root.querySelector('.dz-drift');
  var trafficSvg = root.querySelector('.dz-traffic');
  var topSvg = root.querySelector('.dz-top');
  var trafficG = null;
  var topG = null;
  var city = null;
  var indexEl = root.querySelector('[data-dz-index]');
  var titleEl = root.querySelector('[data-dz-title]');
  var live = root.querySelector('[data-dz-live]');
  var playBtn = root.querySelector('.dz__ctl--play');
  var interval = parseInt(root.dataset.interval, 10) || 6000;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var total = steps.length;
  var current = 0;
  var timer = null;
  var started = 0;
  var remaining = interval;
  var paused = reduce || total < 2;
  var visible = true;
  var held = false;
  root.style.setProperty('--dz-interval', interval + 'ms');

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function show(index, fromUser) {
    var next = (index + total) % total;
    steps.forEach(function (step, i) {
      step.classList.toggle('is-done', i < next);
      if (i === next) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
    current = next;
    light(next);
    // Inside a building, moving to another stage moves to that building; on touch screens a
    // selected plot follows the stepper too.
    if (inside > -1 && inside !== next) enter(next, next > inside ? 1 : -1);
    if (inside < 0 && selected > -1 && selected !== next) { selected = next; paintSelection(); openCard(next); }
    frame();
    if (indexEl) indexEl.textContent = pad(next + 1);
    if (titleEl) titleEl.textContent = steps[next].dataset.title;
    // Keep the current number in view when the row scrolls (phones). Measured in the next frame,
    // after the browser has done its own layout, so it does not force an extra one here.
    var item = stepItems[next];
    if (item) requestAnimationFrame(function () {
      var row = item.parentNode;
      if (row.scrollWidth > row.clientWidth) row.scrollTo({ left: item.offsetLeft - row.clientWidth / 2 + item.offsetWidth / 2, behavior: reduce ? 'auto' : 'smooth' });
    });
    // Restart the progress bar on the new stage (restarting the animation itself, no reflow).
    var bar = steps[next].querySelector('.dz__step-bar > span');
    if (bar && bar.getAnimations) bar.getAnimations().forEach(function (a) { a.cancel(); a.play(); });
    if (fromUser && live) live.textContent = steps[next].dataset.title;
    remaining = interval;
    schedule();
  }

  // ---------- The city ----------
  // World units: 1 unit = one window bay. Each stage is one building on a 6 x 6 plot, streets 2
  // wide, four plots across. Isometric projection: x runs down-right, y down-left, z up.
  var NS = 'http://www.w3.org/2000/svg';
  var U = 10;
  var COS = 0.866;
  var BLOCK = 6;
  var STREET = 2;
  var COLS = 4;
  var compactMq = window.matchMedia('(max-width: 989px)');
  function compact() { return compactMq.matches; }
  function P(x, y, z) { return [(x - y) * COS * U, (x + y) * 0.5 * U - (z || 0) * U]; }
  function pts(list) { return list.map(function (p) { return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' '); }
  function add(tag, attrs, parent) {
    var node = document.createElementNS(NS, tag);
    for (var k in attrs) node.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(node);
    return node;
  }
  function quad(parent, cls, a, b, c, d) { return add('polygon', { class: cls, points: pts([a, b, c, d]) }, parent); }
  // A box: left face (front-left), right face (front-right) and top; windows optional.
  // Returns its polygons so a stage's detail can restyle them.
  function box(parent, x0, y0, x1, y1, z0, z1, windows) {
    var made = [];
    made.push(quad(parent, 'f-l', P(x0, y1, z0), P(x1, y1, z0), P(x1, y1, z1), P(x0, y1, z1)));
    made.push(quad(parent, 'f-r', P(x1, y0, z0), P(x1, y1, z0), P(x1, y1, z1), P(x1, y0, z1)));
    if (windows && z1 - z0 > 0.8) {
      made.push(quad(parent, 'w-l', P(x0 + 0.2, y1, z0 + 0.3), P(x1 - 0.2, y1, z0 + 0.3), P(x1 - 0.2, y1, z1 - 0.25), P(x0 + 0.2, y1, z1 - 0.25)));
      made.push(quad(parent, 'w-r', P(x1, y0 + 0.2, z0 + 0.3), P(x1, y1 - 0.2, z0 + 0.3), P(x1, y1 - 0.2, z1 - 0.25), P(x1, y0 + 0.2, z1 - 0.25)));
    }
    made.push(quad(parent, 'f-t', P(x0, y0, z1), P(x1, y0, z1), P(x1, y1, z1), P(x0, y1, z1)));
    return made;
  }
  // Building shapes (plot-local boxes: x0, y0, x1, y1, z0, z1), varied so the skyline differs.
  // Fabrication (6th) is stacked modules and Demolition (11th) is losing its top.
  function shape(i) {
    var lift = (i * 5) % 3;
    if (i === 5) return { boxes: [[1.3, 1.3, 4.7, 4.7, 0, 1.7], [1.3, 1.3, 4.7, 4.7, 1.95, 3.65], [1.3, 1.3, 4.7, 4.7, 3.9, 5.6]] };
    if (i === 10) return { boxes: [[1, 1, 5, 5, 0, 3], [1.6, 1.6, 4.4, 4.4, 3, 5.6]], ghostTop: true };
    switch (i % 4) {
      case 0: return { boxes: [[1.2, 1.2, 4.8, 4.8, 0, 6.5 + lift], [1.9, 1.9, 4.1, 4.1, 6.5 + lift, 8 + lift]], antenna: true };
      case 1: return { boxes: [[0.8, 0.9, 3, 5.1, 0, 5 + lift], [3.4, 1.4, 5.2, 4.6, 0, 3.4 + lift]] };
      case 2: return { boxes: [[0.7, 1.4, 5.3, 4.6, 0, 3.2 + lift]], helipad: true };
      default: return { boxes: [[0.9, 0.9, 5.1, 5.1, 0, 2.2], [1.6, 1.6, 4.4, 4.4, 2.2, 4.6 + lift], [2.3, 2.3, 3.7, 3.7, 4.6 + lift, 6.4 + lift]] };
    }
  }
  // Each stage's building carries a detail of its own, in the order of the BIM journey:
  // 1 blueprint top, 2 glass massing, 3 model grid on the roof, 4 heat-map facade, 5 drawing sheet,
  // 6 prefab modules (shape), 7 schedule bars, 8 crane, 9 sensor beacons, 10 refurbished band,
  // 11 top being taken down. Details follow the stage's position in the list.
  function identity(i, c) {
    var first = c.boxes[0], last = c.boxes[c.boxes.length - 1], bx = c.bx, by = c.by;
    var x0 = bx + first[0], y0 = by + first[1], x1 = bx + first[2], y1 = by + first[3], h = first[5];
    if (i === 0) c.drawn[c.drawn.length - 1].forEach(function (p) { p.classList.add('is-wire'); });
    if (i === 1) c.el.classList.add('is-glass');
    if (i === 2) {
      var z = last[5], lx0 = bx + last[0], ly0 = by + last[1], lx1 = bx + last[2], ly1 = by + last[3];
      [1 / 3, 2 / 3].forEach(function (t) {
        var a = P(lx0 + (lx1 - lx0) * t, ly0, z), b = P(lx0 + (lx1 - lx0) * t, ly1, z);
        add('line', { class: 'grid-line', x1: a[0], y1: a[1], x2: b[0], y2: b[1] }, c.body);
        a = P(lx0, ly0 + (ly1 - ly0) * t, z); b = P(lx1, ly0 + (ly1 - ly0) * t, z);
        add('line', { class: 'grid-line', x1: a[0], y1: a[1], x2: b[0], y2: b[1] }, c.body);
      });
    }
    if (i === 3) {
      quad(c.body, 'heat', P(x0, y1, 0), P(x1, y1, 0), P(x1, y1, h), P(x0, y1, h));
      quad(c.body, 'heat', P(x1, y0, 0), P(x1, y1, 0), P(x1, y1, h), P(x1, y0, h));
    }
    if (i === 4) {
      quad(c.plaza, 'dz-sheet', P(bx + 0.5, by + 5.05), P(bx + 3.2, by + 5.05), P(bx + 3.2, by + 5.75), P(bx + 0.5, by + 5.75));
      [5.25, 5.45, 5.6].forEach(function (y, n) {
        var a = P(bx + 0.8, by + y), b = P(bx + 2.4 + n * 0.3, by + y);
        add('line', { class: 'dz-sheet-line', x1: a[0], y1: a[1], x2: b[0], y2: b[1] }, c.plaza);
      });
    }
    if (i === 6) {
      [[0.6, 2.4, 5.0], [1.6, 4.0, 5.3], [3.0, 5.3, 5.6]].forEach(function (g, n) {
        quad(c.plaza, 'dz-gantt' + (n === 1 ? ' dz-gantt--b' : ''), P(bx + g[0], by + g[2]), P(bx + g[1], by + g[2]), P(bx + g[1], by + g[2] + 0.18), P(bx + g[0], by + g[2] + 0.18));
      });
    }
    if (i === 7) {
      var top = c.top + 2.2, m0 = P(bx + 5.4, by + 0.6, 0), m1 = P(bx + 5.4, by + 0.6, top);
      var j0 = P(bx + 2.4, by + 0.6, top), j1 = P(bx + 5.8, by + 0.6, top), hk = P(bx + 3.2, by + 0.6, top), hk1 = P(bx + 3.2, by + 0.6, top - 1.6);
      add('line', { class: 'crane', x1: m0[0], y1: m0[1], x2: m1[0], y2: m1[1] }, c.body);
      add('line', { class: 'crane', x1: j0[0], y1: j0[1], x2: j1[0], y2: j1[1] }, c.body);
      add('line', { class: 'crane-hook', x1: hk[0], y1: hk[1], x2: hk1[0], y2: hk1[1] }, c.body);
      c.drawn[c.drawn.length - 1].forEach(function (p) { p.classList.add('is-wire'); });
    }
    if (i === 8) {
      [P(x1, (y0 + y1) / 2, h * 0.75), P((x0 + x1) / 2, y1, h * 0.45), P(x1, y0 + 0.6, h * 0.25)].forEach(function (p, n) {
        // On the traffic layer: a blinking light inside the city would redraw it every frame.
        var dot = add('circle', { class: 'dz-beacon', cx: p[0], cy: p[1], r: 1.1 }, c.traffic);
        dot.style.animationDelay = n * 0.6 + 's';
      });
    }
    if (i === 9) {
      var z0 = h * 0.42, z1 = h * 0.6;
      quad(c.body, 'band', P(x0, y1, z0), P(x1, y1, z0), P(x1, y1, z1), P(x0, y1, z1));
      quad(c.body, 'band', P(x1, y0, z0), P(x1, y1, z0), P(x1, y1, z1), P(x1, y0, z1));
    }
    if (i === 10) {
      [[4.6, 5.1, 0.5], [5.2, 4.5, 0.35], [3.9, 5.5, 0.3]].forEach(function (r) {
        var s = r[2];
        quad(c.plaza, 'dz-rubble', P(bx + r[0], by + r[1], s), P(bx + r[0] + s, by + r[1], s), P(bx + r[0] + s, by + r[1] + s, s), P(bx + r[0], by + r[1] + s, s));
      });
    }
  }

  var stages = [];
  var buildings = [];
  var labels = [];
  var blocks = [];
  var full = null;
  var blockBounds = [];
  var buildingBounds = [];
  var enterLabel = root.dataset.enterLabel || '';
  function draw() {
    var source = root.querySelector('[data-dz-city]');
    if (!svg || !source) return;
    try { stages = JSON.parse(source.textContent); } catch (e) { return; }
    var rows = Math.ceil(stages.length / COLS) || 1;
    var X = COLS * (BLOCK + STREET) + STREET;
    var Y = rows * (BLOCK + STREET) + STREET;
    // Hidden until the arrival plays (only when it will play).
    if (!reduce && 'IntersectionObserver' in window) svg.classList.add('is-waiting');
    city = add('g', { class: 'dz-city' }, svg);
    trafficG = add('g', {}, trafficSvg);
    topG = add('g', {}, topSvg);
    var ground = add('g', {}, city);
    var decor = add('g', {}, city);
    var bLayer = add('g', {}, city);
    var lLayer = add('g', {}, city);
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    function grow(p, pad) {
      minX = Math.min(minX, p[0] - pad); maxX = Math.max(maxX, p[0] + pad);
      minY = Math.min(minY, p[1] - pad); maxY = Math.max(maxY, p[1] + pad);
    }
    function bounds() { return { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }; }
    function growIn(bb, p) {
      bb.minX = Math.min(bb.minX, p[0]); bb.maxX = Math.max(bb.maxX, p[0]);
      bb.minY = Math.min(bb.minY, p[1]); bb.maxY = Math.max(bb.maxY, p[1]);
    }

    var edge = [P(-0.6, -0.6), P(X + 0.6, -0.6), P(X + 0.6, Y + 0.6), P(-0.6, Y + 0.6)];
    quad(ground, 'dz-ground', edge[0], edge[1], edge[2], edge[3]);
    edge.forEach(function (p) { grow(p, 6); });
    for (var c = 0; c <= COLS; c++) {
      var sx = c * (BLOCK + STREET) + STREET / 2;
      add('polyline', { class: 'dz-lane', points: pts([P(sx, 0.4), P(sx, Y - 0.4)]) }, ground);
    }
    for (var r = 0; r <= rows; r++) {
      var sy = r * (BLOCK + STREET) + STREET / 2;
      add('polyline', { class: 'dz-lane', points: pts([P(0.4, sy), P(X - 0.4, sy)]) }, ground);
    }

    var pending = [];
    var plazas = [];
    for (var i = 0; i < COLS * rows; i++) {
      var bx = STREET + (i % COLS) * (BLOCK + STREET);
      var by = STREET + Math.floor(i / COLS) * (BLOCK + STREET);
      var stage = stages[i];
      [[bx - 0.5, by - 0.5], [bx + BLOCK + 0.5, by - 0.5], [bx - 0.5, by + BLOCK + 0.5], [bx + BLOCK + 0.5, by + BLOCK + 0.5]].forEach(function (q) {
        var lp = P(q[0], q[1], 0);
        add('circle', { cx: lp[0], cy: lp[1] - 2, r: 7, fill: 'url(#dz-glow-home)' }, decor);
        add('circle', { class: 'dz-lamp', cx: lp[0], cy: lp[1] - 2, r: 1.1 }, decor);
      });
      if (!stage) {
        // Park: grass, a pond and trees.
        quad(ground, 'dz-grass', P(bx, by), P(bx + BLOCK, by), P(bx + BLOCK, by + BLOCK), P(bx, by + BLOCK));
        var pond = [];
        for (var a = 0; a < 20; a++) {
          var t = a / 20 * Math.PI * 2;
          pond.push(P(bx + 3.6 + Math.cos(t) * 1.5, by + 3.4 + Math.sin(t) * 1.1));
        }
        add('polygon', { class: 'dz-pond', points: pts(pond) }, ground);
        [[0.9, 1], [2.2, 0.8], [5, 1.2], [1, 4.6], [1.6, 2.6], [5.2, 5], [3, 5.3], [4.6, 2.2]].forEach(function (q) {
          pending.push({ depth: bx + q[0] + by + q[1], tree: [bx + q[0], by + q[1]] });
        });
        continue;
      }
      var blockG = add('g', { class: 'dz-block', 'data-stage': i }, ground);
      quad(blockG, 'dz-walk', P(bx, by), P(bx + BLOCK, by), P(bx + BLOCK, by + BLOCK), P(bx, by + BLOCK));
      quad(blockG, 'dz-plaza', P(bx + 0.4, by + 0.4), P(bx + BLOCK - 0.4, by + 0.4), P(bx + BLOCK - 0.4, by + BLOCK - 0.4), P(bx + 0.4, by + BLOCK - 0.4));
      plazas[i] = blockG;
      blocks.push(blockG);
      var sh = shape(i);
      var top = 0, fx0 = Infinity, fy0 = Infinity, fx1 = -Infinity, fy1 = -Infinity;
      sh.boxes.forEach(function (q) {
        top = Math.max(top, q[5]);
        if (q[4] === 0) { fx0 = Math.min(fx0, q[0]); fy0 = Math.min(fy0, q[1]); fx1 = Math.max(fx1, q[2]); fy1 = Math.max(fy1, q[3]); }
      });
      // Cast shadow on the plaza: the light comes from the back left, so it falls to the right.
      var reach = Math.min(top * 0.32, BLOCK - 0.4 - fx1);
      quad(blockG, 'dz-shadow dz-shadow--soft', P(bx + fx1, by + fy0 + 0.1), P(bx + fx1 + Math.min(reach * 1.5, BLOCK - 0.4 - fx1), by + fy0 + 0.1), P(bx + fx1 + Math.min(reach * 1.5, BLOCK - 0.4 - fx1), by + fy1), P(bx + fx1, by + fy1));
      quad(blockG, 'dz-shadow', P(bx + fx1, by + fy0 + 0.3), P(bx + fx1 + reach, by + fy0 + 0.3), P(bx + fx1 + reach, by + fy1), P(bx + fx1, by + fy1));
      var bb = bounds();
      [P(bx, by), P(bx + BLOCK, by), P(bx + BLOCK, by + BLOCK), P(bx, by + BLOCK)].forEach(function (p) { growIn(bb, p); });
      growIn(bb, P(bx + 3, by + 3, top + 4));
      blockBounds[i] = bb;
      pending.push({ depth: bx + by + BLOCK * 2 - 0.5, stage: i, bx: bx, by: by, shape: sh, top: top });
      // Two trees on the plot's open corners.
      pending.push({ depth: bx + 0.4 + by + 5.6, tree: [bx + 0.45, by + 5.55] });
      pending.push({ depth: bx + 5.6 + by + 0.4, tree: [bx + 5.55, by + 0.45] });
    }

    // Back to front so nearer things overlap farther ones.
    pending.sort(function (a, b) { return a.depth - b.depth; });
    pending.forEach(function (it) {
      if (it.tree) {
        var tp = P(it.tree[0], it.tree[1], 0);
        add('ellipse', { class: 'dz-tree-shadow', cx: tp[0] + 2, cy: tp[1] + 1, rx: 4, ry: 2 }, bLayer);
        add('circle', { class: 'dz-tree', cx: tp[0], cy: tp[1] - 4, r: 3.6 }, bLayer);
        add('circle', { class: 'dz-tree-top', cx: tp[0] - 0.8, cy: tp[1] - 5, r: 2 }, bLayer);
        return;
      }
      var stage = stages[it.stage];
      var bx = it.bx, by = it.by;
      // Each building is a button: it opens the building rather than leaving the page.
      var b = add('g', { class: 'dz-b', role: 'button', tabindex: '0', 'data-stage': it.stage, 'aria-label': stage.title + (enterLabel ? ', ' + enterLabel : '') }, bLayer);
      add('title', {}, b).textContent = stage.title;
      var body = add('g', {}, b);
      var boxes = it.shape.boxes.slice().sort(function (p, q) { return p[4] - q[4] || (p[2] + p[3]) - (q[2] + q[3]); });
      var bbB = bounds();
      var drawn = [];
      boxes.forEach(function (q, n) {
        var made = box(body, bx + q[0], by + q[1], bx + q[2], by + q[3], q[4], q[5], true);
        if (it.shape.ghostTop && n === boxes.length - 1) made.forEach(function (p) { p.classList.add('is-ghost'); });
        drawn.push(made);
        [P(bx + q[0], by + q[3], q[4]), P(bx + q[2], by + q[3], q[4]), P(bx + q[2], by + q[1], q[4]), P(bx + q[0], by + q[1], q[5]), P(bx + q[2], by + q[1], q[5]), P(bx + q[0], by + q[3], q[5])].forEach(function (p) { growIn(bbB, p); });
        if (n === 0) {
          // The entrance, lit, at the foot of the front-left face.
          var dx = bx + (q[0] + q[2]) / 2;
          quad(body, 'door', P(dx - 0.45, by + q[3], 0), P(dx + 0.45, by + q[3], 0), P(dx + 0.45, by + q[3], 1.1), P(dx - 0.45, by + q[3], 1.1));
        }
      });
      var last = boxes[boxes.length - 1];
      var cx = bx + (last[0] + last[2]) / 2, cy = by + (last[1] + last[3]) / 2;
      if (it.shape.antenna) {
        var a0 = P(cx, cy, it.top), a1 = P(cx, cy, it.top + 1.8);
        add('line', { class: 'ant', x1: a0[0], y1: a0[1], x2: a1[0], y2: a1[1] }, body);
        add('circle', { class: 'ant-tip', cx: a1[0], cy: a1[1], r: 0.9 }, body);
        growIn(bbB, a1);
      }
      if (it.shape.helipad) {
        var ring = [];
        for (var k = 0; k < 18; k++) {
          var t = k / 18 * Math.PI * 2;
          ring.push(P(cx + Math.cos(t) * 0.9, cy + Math.sin(t) * 0.9, it.top));
        }
        add('polygon', { class: 'pad', points: pts(ring) }, body);
      }
      identity(it.stage, { el: b, body: body, plaza: plazas[it.stage], traffic: trafficG, bx: bx, by: by, boxes: boxes, drawn: drawn, top: it.top });
      if (it.stage === 7) growIn(bbB, P(bx + 5.4, by + 0.6, it.top + 2.2));
      buildingBounds[it.stage] = bbB;
      buildings.push(b);

      // Name sign above the roof, with a numbered pin for touch screens and a tick once visited.
      var anchor = P(cx, cy, it.top + (it.shape.antenna ? 1.8 : 0) + (it.stage === 7 ? 2.2 : 0));
      var num = (it.stage < 9 ? '0' : '') + (it.stage + 1);
      var lbl = add('g', { class: 'dz-lbl', 'data-stage': it.stage }, lLayer);
      var stemLine = add('line', { class: 'stem', x1: anchor[0], y1: anchor[1], x2: anchor[0], y2: anchor[1] - 8 }, lbl);
      var plate = add('g', { class: 'plate' }, lbl);
      var text = add('text', { x: anchor[0], y: anchor[1] - 11, 'text-anchor': 'middle' }, plate);
      add('tspan', { class: 'num' }, text).textContent = num + ' ';
      add('tspan', {}, text).textContent = stage.title;
      var pin = add('g', { class: 'pin' }, lbl);
      add('circle', { cx: anchor[0], cy: anchor[1] - 9, r: 8.5 }, pin);
      add('text', { x: anchor[0], y: anchor[1] - 6.4, 'text-anchor': 'middle' }, pin).textContent = num;
      labels[it.stage] = { g: lbl, text: text, plate: plate, stem: stemLine, anchor: anchor, lift: 0 };
      grow(P(bx + it.shape.boxes[0][0], by + it.shape.boxes[0][1], it.top), 12);
    });

    // Sign plates and ticks, then lift any sign that would cover one already placed (nearest first).
    var placed = [];
    labels.filter(Boolean).forEach(function (l) {
      var b = l.text.getBBox();
      l.box = { x: b.x - 4, y: b.y - 2.5, w: b.width + 8, h: b.height + 5 };
      l.plate.insertBefore(add('rect', { x: l.box.x, y: l.box.y, width: l.box.w, height: l.box.h, rx: 3 }), l.text);
      var tick = add('g', { class: 'tick' }, l.plate);
      var tx = l.box.x + l.box.w, ty = l.box.y;
      add('circle', { cx: tx, cy: ty, r: 3.6 }, tick);
      add('path', { d: 'M' + (tx - 1.6) + ' ' + ty + ' l1.1 1.1 l2.1 -2.2' }, tick);
    });
    labels.filter(Boolean).sort(function (a, b) { return b.box.y - a.box.y; }).forEach(function (l) {
      function hits() {
        var y = l.box.y - l.lift;
        return placed.some(function (o) {
          var oy = o.box.y - o.lift;
          return l.box.x < o.box.x + o.box.w + 3 && o.box.x < l.box.x + l.box.w + 3 && y < oy + o.box.h + 3 && oy < y + l.box.h + 3;
        });
      }
      while (hits() && l.lift < 160) l.lift += 4;
      if (l.lift) {
        l.plate.setAttribute('transform', 'translate(0 ' + (-l.lift) + ')');
        l.stem.setAttribute('y2', l.anchor[1] - 8 - l.lift);
      }
      grow([l.anchor[0], l.box.y - l.lift], 10);
      placed.push(l);
    });
    full = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };

    // A few cars driving along the streets.
    if (!reduce) {
      var colours = ['#4fc8f4', '#f2c94c', '#ef6a5a', '#e8eef3', '#7fd17a'];
      [[1, true, 9], [3, false, 12], [2, true, 14], [0, false, 10], [4, true, 11]].forEach(function (c, n) {
        var along = c[1];
        var line = c[0] * (BLOCK + STREET) + STREET / 2 + (n % 2 ? 0.45 : -0.45);
        if (!along && c[0] > rows) return;
        var from = along ? P(line, 0.3) : P(0.3, line);
        var to = along ? P(line, Y - 0.3) : P(X - 0.3, line);
        if (n % 2) { var tmp = from; from = to; to = tmp; }
        var car = add('g', {}, trafficG);
        add('rect', { class: 'dz-car', x: -3.5, y: -1.6, width: 7, height: 3.2, rx: 1.4, fill: colours[n % colours.length] }, car);
        add('circle', { class: 'dz-car-light', cx: 3.4, cy: 0, r: 0.8 }, car);
        add('animateMotion', { dur: c[2] + 's', repeatCount: 'indefinite', rotate: 'auto', path: 'M' + from[0] + ',' + from[1] + ' L' + to[0] + ',' + to[1], begin: (-n * 2.3) + 's' }, car);
      });
    }

    // Arrival order: farthest buildings rise first.
    buildings.forEach(function (b, k) {
      b.style.setProperty('--d', k * 70 + 'ms');
      var l = labels[+b.getAttribute('data-stage')];
      if (l) l.g.style.setProperty('--d', k * 70 + 'ms');
    });

    // Mouse: hover raises a building; a click zooms to it and its card offers the way in. Keyboard
    // focus raises it too; Enter or Space goes inside. Touch: a tap selects a building (or its pin)
    // the same way; a second click or tap on it goes in; dragging moves around the city.
    svg.addEventListener('pointermove', function (e) {
      if (inside < 0 && selected < 0 && !compact() && e.pointerType === 'mouse') setHot(e.target.closest ? e.target.closest('.dz-b') : null);
    });
    svg.addEventListener('pointerleave', function () { if (selected < 0) setHot(null); });
    svg.addEventListener('focusin', function (e) { if (inside < 0) setHot(e.target.closest ? e.target.closest('.dz-b') : null); });
    svg.addEventListener('focusout', function (e) { if (!svg.contains(e.relatedTarget)) setHot(null); });
    svg.addEventListener('click', function (e) {
      if (suppressClick) { suppressClick = false; return; }
      var hit = e.target.closest ? (e.target.closest('.dz-b') || e.target.closest('.dz-lbl')) : null;
      var i = hit ? +hit.getAttribute('data-stage') : -1;
      if (i < 0) { deselect(); return; }
      if (selected === i) enter(i);
      else select(i);
    });
    svg.addEventListener('keydown', function (e) {
      var b = e.target.closest ? e.target.closest('.dz-b') : null;
      if (b && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); enter(+b.getAttribute('data-stage'), 0, true); }
    });
    svg.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    if (map) {
      map.addEventListener('pointermove', drift);
      map.addEventListener('pointerleave', function () { driftTo(0, 0); });
    }
  }
  // Hover or focus raises a building: a copy of it (and of its sign) is drawn on the top layer,
  // above the veil, and rises there; the original stays put, hidden. The city is never redrawn
  // for the dim (the veil fades by opacity) and no element is moved.
  var hotB = null;
  function setHot(b) {
    if (b === hotB) return;
    var prev = hotB;
    hotB = b;
    if (prev) hot(prev, labels[+prev.getAttribute('data-stage')], false);
    if (b) hot(b, labels[+b.getAttribute('data-stage')], true);
    if (!b) cam.classList.remove('has-hot');
  }
  function hot(b, l, on) {
    if (on) {
      var cb, cl;
      if (b._settling) {
        // Back on a building whose copy is still settling: that copy rises again (a fresh copy
        // would be taken from the hidden original and be hidden too).
        clearTimeout(b._settling.timer);
        cb = b._settling.cb;
        cl = b._settling.cl;
        b._settling = null;
      } else {
        cb = b.cloneNode(true);
        ['role', 'tabindex', 'aria-label'].forEach(function (a) { cb.removeAttribute(a); });
        cl = l.g.cloneNode(true);
        cb.classList.remove('is-lifted', 'is-pressed');
        cl.classList.remove('is-lifted');
        topG.appendChild(cb);
        topG.appendChild(cl);
      }
      b._copy = cb;
      l.g._copy = cl;
      b.classList.add('is-lifted');
      l.g.classList.add('is-lifted');
      cam.classList.add('has-hot');
      requestAnimationFrame(function () {
        if (hotB !== b || b._copy !== cb) return;
        cb.classList.add('is-hot');
        cl.classList.add('is-hot');
      });
    } else {
      var cb2 = b._copy, cl2 = l.g._copy;
      b._copy = null;
      l.g._copy = null;
      if (!cb2) return;
      cb2.classList.remove('is-hot', 'is-float');
      cl2.classList.remove('is-hot', 'is-float');
      // The copy settles back, then the original takes over again.
      b._settling = { cb: cb2, cl: cl2, timer: setTimeout(function () {
        b._settling = null;
        cb2.remove();
        cl2.remove();
        if (!b._copy) b.classList.remove('is-lifted');
        if (!l.g._copy) l.g.classList.remove('is-lifted');
      }, 400) };
    }
  }
  // Only the building that goes dark and the one that lights up change class: a class change in
  // the city makes the browser re-check styles there, so the fewer the better.
  var litNow = -1;
  function light(stage) {
    if (stage === litNow) return;
    [litNow, stage].forEach(function (k) {
      if (k < 0) return;
      var on = k === stage;
      var b = buildingEl(k);
      if (b) b.classList.toggle('is-lit', on);
      if (labels[k]) labels[k].g.classList.toggle('is-lit', on);
      var g = blocks.filter(function (x) { return +x.getAttribute('data-stage') === k; })[0];
      if (g) g.classList.toggle('is-lit', on);
    });
    litNow = stage;
  }
  function buildingEl(i) { return buildings.filter(function (b) { return +b.getAttribute('data-stage') === i; })[0]; }

  // Desktop: the city drifts a few pixels with the pointer, so it feels like a model. One
  // transform write per frame on its own wrapper; the map's position is read once per hover.
  var mapBox = null;
  var driftFrame = 0;
  var driftXY = [0, 0];
  function drift(e) {
    if (reduce || compact() || inside > -1 || e.pointerType !== 'mouse') return;
    if (!mapBox) mapBox = map.getBoundingClientRect();
    driftTo(((e.clientX - mapBox.left) / mapBox.width - 0.5) * -10, ((e.clientY - mapBox.top) / mapBox.height - 0.5) * -7);
  }
  function driftTo(x, y) {
    driftXY = [x, y];
    if (!x && !y) mapBox = null;
    if (driftFrame) return;
    driftFrame = requestAnimationFrame(function () {
      driftFrame = 0;
      drifter.style.transform = driftXY[0] || driftXY[1] ? 'translate(' + driftXY[0].toFixed(1) + 'px, ' + driftXY[1].toFixed(1) + 'px)' : '';
    });
  }
  window.addEventListener('scroll', function () { mapBox = null; }, { passive: true });

  // Arrival: the first time the city is on screen its buildings rise, farthest first. Started by
  // whichever comes first: the visibility observer, a scroll, or the city already being on screen
  // when the page loads (so it never stays hidden if one of them does not report).
  var arrived = false;
  function arriveIfShown() {
    if (arrived) return;
    var r = root.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.85 && r.bottom > 0) arrive();
  }
  window.addEventListener('scroll', arriveIfShown, { passive: true });
  function arrive() {
    if (arrived || !svg) return;
    arrived = true;
    window.removeEventListener('scroll', arriveIfShown);
    svg.classList.remove('is-waiting');
    if (reduce) return;
    if (compact() && drifter.animate) {
      drifter.animate([{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'none' }], { duration: 750, easing: 'cubic-bezier(0.2, 0.9, 0.2, 1)' });
      return;
    }
    svg.classList.add('is-arriving');
    setTimeout(function () { svg.classList.remove('is-arriving'); }, buildings.length * 70 + 1300);
  }

  // ---------- Touch: selecting and dragging ----------
  var selected = -1;
  var card = root.querySelector('[data-dz-card]');
  var cardTimer = null;
  function select(i) {
    if (!stages[i]) return;
    selected = i;
    if (current !== i) show(i, true);
    else light(i);
    paintSelection();
    openCard(i);
    stop();
    frame();
    // While zoomed to, the building rises and floats above the city.
    var fb = buildingEl(i);
    setHot(fb);
    clearTimeout(floatTimer);
    floatTimer = setTimeout(function () {
      if (selected !== i || !fb || !fb._copy) return;
      fb._copy.classList.add('is-float');
      var lb = labels[i] && labels[i].g._copy;
      if (lb) lb.classList.add('is-float');
    }, 450);
  }
  var floatTimer = null;
  function deselect(keepFrame) {
    if (selected < 0) return;
    selected = -1;
    clearTimeout(floatTimer);
    setHot(null);
    paintSelection();
    closeCard();
    if (!keepFrame) frame();
    schedule();
  }
  var selNow = -1;
  function paintSelection() {
    if (selNow === selected) return;
    if (labels[selNow]) labels[selNow].g.classList.remove('is-sel');
    if (labels[selected]) labels[selected].g.classList.add('is-sel');
    selNow = selected;
  }
  function openCard(i) {
    if (!card) return;
    clearTimeout(cardTimer);
    card.querySelector('[data-dz-card-num]').textContent = pad(i + 1);
    card.querySelector('[data-dz-card-meta]').textContent = servicesText.replace('[count]', stages[i].services.length);
    card.querySelector('[data-dz-card-title]').textContent = stages[i].title;
    card.hidden = false;
    map.classList.add('has-card');
    requestAnimationFrame(function () { card.classList.add('is-open'); });
  }
  function closeCard() {
    if (!card) return;
    card.classList.remove('is-open');
    map.classList.remove('has-card');
    cardTimer = setTimeout(function () { card.hidden = true; }, 450);
  }
  if (card) {
    // A click with detail 0 came from the keyboard.
    card.querySelector('[data-dz-card-enter]').addEventListener('click', function (e) { if (selected > -1) enter(selected, 0, e.detail === 0); });
    card.querySelector('[data-dz-card-close]').addEventListener('click', function () { deselect(); });
  }

  // Dragging moves the camera (touch screens). On touch the page keeps vertical scrolling, so a
  // finger drags the city sideways; a mouse can drag both ways. A drag never counts as a tap.
  // While dragging (and coasting) the camera layer is only translated; the view is committed to
  // the SVG once at the end, so the city is not redrawn on every finger movement.
  var drag = { active: false };
  var suppressClick = false;
  var pressed = null;
  var coastFrame = 0;
  var offset = [0, 0];
  function unpress() { if (pressed) pressed.classList.remove('is-pressed'); pressed = null; }
  function onDown(e) {
    if (!compact() || inside > -1 || !view || e.button > 0) return;
    settleCamera();
    drag = { active: true, id: e.pointerId, x: e.clientX, y: e.clientY, lx: e.clientX, ly: e.clientY, moved: false, vx: 0, vy: 0, t: performance.now(), touch: e.pointerType === 'touch' };
    pressed = e.target.closest ? e.target.closest('.dz-b') : null;
    if (pressed) pressed.classList.add('is-pressed');
  }
  function onMove(e) {
    if (!drag.active || e.pointerId !== drag.id) return;
    var dx = e.clientX - drag.lx, dy = drag.touch ? 0 : e.clientY - drag.ly;
    if (!drag.moved && Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) > 8) {
      drag.moved = true;
      unpress();
    }
    if (drag.moved) {
      moveBy(dx, dy);
      var now = performance.now(), dt = Math.max(8, now - drag.t);
      drag.vx = dx / dt; drag.vy = dy / dt; drag.t = now;
    }
    drag.lx = e.clientX; drag.ly = e.clientY;
  }
  function onUp(e) {
    if (!drag.active || e.pointerId !== drag.id) return;
    drag.active = false;
    unpress();
    if (drag.moved) {
      suppressClick = true;
      setTimeout(function () { suppressClick = false; }, 60);
      coast(drag.vx * 16, drag.vy * 16);
    }
  }
  function onCancel() { drag.active = false; unpress(); }
  // Keeps the view over the city. In a direction where the whole city already fits, the view
  // stays where it is (it was framed with room for the hint or the card).
  function clampView(v) {
    var x = v.w >= full.w ? view.x : Math.min(full.x + full.w - v.w * 0.7, Math.max(full.x - v.w * 0.3, v.x));
    var y = v.h >= full.h ? view.y : Math.min(full.y + full.h - v.h * 0.7, Math.max(full.y - v.h * 0.3, v.y));
    return { x: x, y: y, w: v.w, h: v.h };
  }
  // Shift the camera layer by screen pixels, kept inside the city's bounds.
  function moveBy(dx, dy) {
    var k = view.w / mapSize.w;
    var want = clampView({ x: view.x - (offset[0] + dx) * k, y: view.y - (offset[1] + dy) * k, w: view.w, h: view.h });
    offset = [(view.x - want.x) / k, (view.y - want.y) / k];
    cam.style.transform = 'translate(' + offset[0].toFixed(1) + 'px, ' + offset[1].toFixed(1) + 'px)';
  }
  function commitOffset() {
    if (!offset[0] && !offset[1]) return;
    var k = view.w / mapSize.w;
    view = { x: view.x - offset[0] * k, y: view.y - offset[1] * k, w: view.w, h: view.h };
    offset = [0, 0];
    cam.style.transform = '';
    setBox(view);
  }
  // Momentum after a flick (pixels per frame), easing out, then committed.
  function coast(vx, vy) {
    if (reduce) { commitOffset(); return; }
    (function step() {
      if (drag.active) return;
      if (Math.abs(vx) + Math.abs(vy) < 0.2) { coastFrame = 0; commitOffset(); return; }
      moveBy(vx, vy);
      vx *= 0.92; vy *= 0.92;
      coastFrame = requestAnimationFrame(step);
    })();
  }
  function cancelCoast() {
    if (coastFrame) cancelAnimationFrame(coastFrame);
    coastFrame = 0;
    commitOffset();
  }

  // ---------- Inside a building ----------
  // The camera dives into the building, which splits into its floors: slabs cut from it on
  // screen fly to the floors of the cut-away, where the stage's services light up. On touch
  // screens the cut-away takes over the screen, swipes move between buildings, and the phone's
  // back gesture returns to the city. Visited buildings are remembered for this visit.
  var inside = -1;
  var insideEl = root.querySelector('.dz-inside');
  var floorsEl = root.querySelector('[data-dz-floors]');
  var floorTpl = root.querySelector('[data-dz-floor-tpl]');
  var liftEl = root.querySelector('.dz-inside__tower');
  var dotsEl = root.querySelector('[data-dz-dots]');
  var progressEl = root.querySelector('[data-dz-progress]');
  var hintEl = root.querySelector('[data-dz-hint]');
  var servicesText = '';
  try { servicesText = JSON.parse(root.querySelector('[data-dz-labels]').textContent).services || ''; } catch (e) {}
  var letters = ['A', 'B', 'C', 'D', 'E', 'F'];
  var openTimer = null;
  var pushed = false;
  var visitedKey = 'dz-visited-home';
  var visited = {};
  try { (JSON.parse(sessionStorage.getItem(visitedKey)) || []).forEach(function (k) { visited[k] = 1; }); } catch (e) {}
  function markVisited(i) {
    if (visited[i]) return;
    visited[i] = 1;
    try { sessionStorage.setItem(visitedKey, JSON.stringify(Object.keys(visited).map(Number))); } catch (e) {}
    paintVisited(i);
  }
  // All signs at start-up; afterwards only the one just visited.
  function paintVisited(only) {
    labels.forEach(function (l, k) { if (l && (only === undefined || k === only)) l.g.classList.toggle('is-visited', !!visited[k]); });
    var n = Object.keys(visited).filter(function (k) { return stages[k]; }).length;
    if (progressEl) progressEl.textContent = n ? (progressEl.dataset.text || '').replace('[n]', n).replace('[total]', stages.length) : '';
  }
  // ---------- Room pictures ----------
  // Each service's floor shows a small scene of the work itself: a board on the wall and a model
  // on the floor, drawn in the city's palette. Scenes are matched to the service by its name
  // (so renamed services still find theirs), falling back to the default order. Each scene is
  // wrapped in its own class, which its hover animation uses.
  var art = (function () {
    function f(n) { return Math.round(n * 10) / 10; }
    function pts(a) { return a.map(function (p) { return f(p[0]) + ',' + f(p[1]); }).join(' '); }
    function poly(a, c) { return '<polygon class="' + c + '" points="' + pts(a) + '"/>'; }
    function rect(x, y, w, h, c, rx) { return '<rect class="' + c + '" x="' + f(x) + '" y="' + f(y) + '" width="' + f(w) + '" height="' + f(h) + '"' + (rx ? ' rx="' + rx + '"' : '') + '/>'; }
    function line(x1, y1, x2, y2, c) { return '<line class="' + c + '" x1="' + f(x1) + '" y1="' + f(y1) + '" x2="' + f(x2) + '" y2="' + f(y2) + '"/>'; }
    function circ(cx, cy, r, c) { return '<circle class="' + c + '" cx="' + f(cx) + '" cy="' + f(cy) + '" r="' + f(r) + '"/>'; }
    function ell(cx, cy, rx, ry, c) { return '<ellipse class="' + c + '" cx="' + f(cx) + '" cy="' + f(cy) + '" rx="' + f(rx) + '" ry="' + f(ry) + '"/>'; }
    function path(d, c) { return '<path class="' + c + '" d="' + d + '"/>'; }
    function text(x, y, s, c) { return '<text class="' + (c || 'tx') + '" x="' + f(x) + '" y="' + f(y) + '">' + s + '</text>'; }
    // An isometric block standing on (cx, by): w to the left, d to the right, h tall; t picks the tone.
    function box(cx, by, w, d, h, t) {
      return poly([[cx, by], [cx - w, by - w / 2], [cx - w, by - w / 2 - h], [cx, by - h]], t + 'l') +
        poly([[cx, by], [cx + d, by - d / 2], [cx + d, by - d / 2 - h], [cx, by - h]], t + 'r') +
        poly([[cx, by - h], [cx - w, by - w / 2 - h], [cx - w + d, by - w / 2 - d / 2 - h], [cx + d, by - d / 2 - h]], t + 't');
    }
    function wire(cx, by, w, d, h) {
      return poly([[cx, by - h], [cx - w, by - w / 2 - h], [cx - w + d, by - w / 2 - d / 2 - h], [cx + d, by - d / 2 - h]], 'wf') +
        line(cx, by, cx, by - h, 'wl') + line(cx - w, by - w / 2, cx - w, by - w / 2 - h, 'wl') + line(cx + d, by - d / 2, cx + d, by - d / 2 - h, 'wl') +
        poly([[cx, by], [cx - w, by - w / 2], [cx - w + d, by - w / 2 - d / 2], [cx + d, by - d / 2]], 'wf');
    }
    function cyl(cx, by, r, h, t) { return rect(cx - r, by - h, r * 2, h, t + 'r') + ell(cx, by, r, r / 2, t + 'r') + ell(cx, by - h, r, r / 2, t + 't'); }
    function shadow(cx, by, rx) { return ell(cx, by - 1, rx * 0.8, rx * 0.3, 'sh'); }
    // The wall board: everything inside is drawn in its own 76 x 44 space.
    function board(inner) { return '<g class="art-board"><rect class="bd" x="82" y="9" width="76" height="44" rx="3"/><g transform="translate(82 9)">' + inner + '</g></g>'; }
    function prop(inner) { return '<g class="art-prop">' + inner + '</g>'; }
    function rows(x, y, n, w, gap, c) { var s = ''; for (var i = 0; i < n; i++) s += line(x, y + i * gap, x + (i % 2 ? w * 0.7 : w), y + i * gap, c || 'pl'); return s; }
    function check(x, y, s, c) { return path('M' + f(x) + ' ' + f(y) + 'l' + f(s * 0.35) + ' ' + f(s * 0.35) + 'l' + f(s * 0.65) + ' -' + f(s * 0.7), c || 'ck'); }
    function coins(cx, by, n) { var s = ''; for (var i = 0; i < n; i++) s += cyl(cx, by - i * 3, 6, 3, 'y'); return s; }
    // Shared room: a hanging lamp and a plant at the sides (seen on wide floors).
    var room = line(56, 0, 56, 13, 'cord') + path('M51 17a5 4 0 0 1 10 0z', 'lampc') +
      rect(204, 88, 12, 10, 'pot', 1.5) + circ(206, 82, 6, 'g1') + circ(214, 80, 7, 'g2') + circ(210, 74, 6, 'g1');

    var scenes = [
      // Programming
      { k: 'eir', re: /requirement|\beir\b/i, d: board(
          [0, 1, 2, 3].map(function (i) { return rect(8, 7 + i * 9, 6, 6, 'cb', 1) + (i < 3 ? check(9, 10 + i * 9, 4) : '') + line(19, 10 + i * 9, i % 2 ? 52 : 64, 10 + i * 9, 'lw'); }).join('')) +
        prop(shadow(120, 100, 26) + box(122, 101, 20, 15, 3, 'p') + box(120, 97, 19, 14, 3, 'p') + box(123, 93, 18, 13, 3, 'p') + line(130, 82, 146, 90, 'pen')) },
      { k: 'std', re: /standard/i, d: board(
          path('M38 6 54 11v11c0 9-7 14-16 17-9-3-16-8-16-17V11z', 'shd') + check(31, 22, 13, 'ckb') + text(56, 40, 'ISO', 'tx sm')) +
        prop(shadow(120, 101, 34) + line(88, 101, 152, 101, 'ln') + box(98, 99, 8, 8, 13, 'b') + box(120, 99, 8, 8, 13, 'a') + box(142, 99, 8, 8, 13, 'b')) },
      { k: 'bep', re: /execution plan|\bbep\b/i, d: board(
          rect(6, 17, 14, 10, 'nd', 2) + rect(31, 6, 14, 10, 'nd', 2) + rect(31, 28, 14, 10, 'nd', 2) + path('M60 15l7 7-7 7-7-7z', 'nda') +
          path('M20 22h5v-11h6M25 22v11h6M45 11h8M45 33h8v-4', 'ln')) +
        prop(shadow(120, 100, 32) + poly([[92, 100], [104, 94], [150, 94], [140, 100]], 'road') + line(100, 97, 146, 97, 'dash') +
          line(102, 96, 102, 80, 'pole') + poly([[102, 80], [112, 83], [102, 86]], 'fa') + line(124, 96, 124, 76, 'pole') + poly([[124, 76], [134, 79], [124, 82]], 'fy') + line(144, 96, 144, 82, 'pole') + poly([[144, 82], [152, 85], [144, 88]], 'fg')) },
      // Conceptual design
      { k: 'mass', re: /massing|site stud/i, d: board(
          path('M8 38Q38 -2 68 38', 'arc') + circ(52, 14, 4.5, 'sun') + text(8, 12, 'N', 'tx sm') + path('M10 14v8m-2-3 2 3 2-3', 'lw')) +
        prop(shadow(120, 101, 34) + box(120, 103, 30, 30, 2, 'g') + box(110, 96, 10, 10, 8, 'p') + box(130, 96, 9, 9, 20, 'p') + box(122, 87, 7, 7, 6, 'p')) },
      { k: 'opt', re: /option|compar/i, d: board(
          line(38, 6, 38, 38, 'lf') + text(15, 11, 'A', 'tx') + text(53, 11, 'B', 'tx') + rect(8, 24, 22, 12, 'sk') + rect(13, 18, 10, 6, 'sk') + rect(48, 14, 9, 22, 'sk') + rect(58, 24, 9, 12, 'sk')) +
        prop(shadow(104, 101, 16) + shadow(138, 101, 16) + box(104, 101, 10, 10, 5, 'm') + box(104, 91, 9, 9, 7, 'p') + box(138, 101, 10, 10, 5, 'm') + box(138, 91, 6, 6, 18, 'a') + text(98, 109, 'A', 'tx sm') + text(135, 109, 'B', 'tx sm')) },
      { k: 'viz', re: /visuali|render|concept/i, d: board(
          rect(4, 4, 68, 36, 'sky', 2) + circ(58, 13, 4, 'sun') + rect(12, 22, 10, 18, 'bld') + rect(24, 14, 12, 26, 'bld2') + rect(38, 26, 9, 14, 'bld') + rect(4, 36, 68, 4, 'gr')) +
        prop(shadow(120, 101, 14) + line(120, 86, 110, 101, 'leg') + line(120, 86, 130, 101, 'leg') + line(120, 86, 120, 101, 'leg') + rect(110, 74, 20, 12, 'cam', 2) + circ(120, 80, 4, 'lens') + rect(113, 71, 6, 3, 'cam', 1)) },
      // Detailed project
      { k: 'arch', re: /architect/i, d: board(
          rect(8, 6, 60, 32, 'plan') + line(30, 6, 30, 24, 'pw') + line(8, 22, 30, 22, 'pw') + line(48, 22, 48, 38, 'pw') + line(48, 22, 68, 22, 'pw') + path('M30 30a8 8 0 0 1 8-8', 'lw') + rect(14, 6, 10, 1.6, 'af')) +
        prop(shadow(120, 101, 22) + box(120, 101, 16, 16, 34, 'b') + [0, 1, 2, 3].map(function (i) { return line(120, 92 - i * 8, 104, 84 - i * 8, 'win') + line(120, 92 - i * 8, 136, 84 - i * 8, 'win'); }).join('')) },
      { k: 'mep', re: /\bmep\b|mechanical|plumb/i, d: board(
          path('M4 12h24v18h20v-8h24', 'pr') + path('M4 22h14v12h40', 'pb') + path('M4 32h8v-24h34v10h22', 'py') + circ(28, 21, 2.5, 'vlv') + circ(46, 8, 2.5, 'vlv')) +
        prop(shadow(120, 101, 32) + box(132, 99, 26, 8, 9, 'm') + cyl(100, 100, 4, 18, 'r') + cyl(110, 102, 4, 13, 'a')) },
      { k: 'str', re: /structur/i, d: board(
          path('M24 8h28v5H41v18h11v5H24v-5h11V13H24z', 'ibm') + line(16, 8, 16, 36, 'dim') + line(14, 8, 18, 8, 'dim') + line(14, 36, 18, 36, 'dim') + text(56, 26, 'HEB', 'tx sm')) +
        prop(shadow(120, 101, 28) + wire(120, 102, 20, 20, 14) + wire(120, 88, 20, 20, 13) + line(120, 102, 120, 75, 'col') + line(100, 92, 100, 65, 'col') + line(140, 92, 140, 65, 'col')) },
      // Analysis
      { k: 'clash', re: /clash/i, d: board(
          rect(4, 18, 68, 6, 'pipeR') + rect(33, 2, 9, 40, 'pipeB') + circ(37.5, 21, 9, 'clash') + text(35.5, 24.5, '!', 'tx warn')) +
        prop(shadow(120, 101, 26) + box(128, 99, 24, 6, 7, 'm') + box(114, 104, 6, 22, 12, 'a') + ell(121, 88, 12, 6, 'clash')) },
      { k: 'energy', re: /energy|sustain/i, d: board(
          circ(12, 11, 5, 'sun') + [10, 15, 21, 27].map(function (h, i) { return rect(26 + i * 11, 38 - h, 7, h, i === 3 ? 'barG' : 'barG2', 1); }).join('') + path('M24 30 L64 9', 'ln')) +
        prop(shadow(120, 101, 24) + box(120, 101, 18, 18, 20, 'b') + poly([[120, 81], [102, 72], [120, 63], [138, 72]], 'solar') + line(111, 76.5, 129, 67.5, 'sg') + line(111, 67.5, 129, 76.5, 'sg') + path('M146 96c0-8 6-12 12-12 0 8-5 12-12 12z', 'leaf')) },
      { k: 'code', re: /code|complian|sbc/i, d: board(
          rect(10, 4, 34, 36, 'pp', 1) + rows(15, 11, 6, 22, 4.5) + circ(56, 24, 11, 'stampG') + check(50, 24, 12, 'ckw') + text(48, 41, 'SBC', 'tx sm')) +
        prop(shadow(120, 101, 22) + box(120, 101, 16, 16, 24, 'b') + circ(120, 64, 7, 'okB') + check(116.5, 64, 7, 'ckw')) },
      // Documentation
      { k: 'ga', re: /construction drawing|\bga\b|general arrangement/i, d: board(
          rect(3, 3, 70, 38, 'blue', 1) + rect(8, 8, 40, 26, 'bpw') + line(24, 8, 24, 22, 'bpl') + line(8, 22, 48, 22, 'bpl') + line(36, 22, 36, 34, 'bpl') + rect(52, 28, 17, 10, 'bpw') + rows(54, 31, 2, 12, 3.5, 'bpl')) +
        prop(shadow(120, 101, 26) + rect(100, 92, 34, 8, 'tube', 4) + ell(134, 96, 2.5, 4, 'tubeE') + rect(108, 84, 32, 8, 'tube2', 4) + ell(140, 88, 2.5, 4, 'tubeE')) },
      { k: 'boq', re: /\bboq\b|quantit|take-?off/i, d: board(
          rect(4, 4, 68, 36, 'pp', 1) + rect(4, 4, 68, 7, 'th', 1) + line(30, 4, 30, 40, 'pl') + line(52, 4, 52, 40, 'pl') + [0, 1, 2].map(function (i) { return line(4, 18 + i * 8, 72, 18 + i * 8, 'pl') + text(55, 16.5 + i * 8, ['120', '48', '9.6'][i], 'tx num') + text(33, 16.5 + i * 8, ['m³', 'm²', 't'][i], 'tx num'); }).join('')) +
        prop(shadow(118, 101, 28) + box(108, 101, 8, 14, 5, 'o') + box(108, 96, 8, 14, 5, 'o') + box(122, 92, 8, 14, 5, 'o') + box(126, 101, 8, 14, 5, 'o') + line(96, 104, 146, 104, 'tape') + [0, 1, 2, 3, 4, 5].map(function (i) { return line(98 + i * 9, 102.5, 98 + i * 9, 105.5, 'tick'); }).join('')) },
      { k: 'spec', re: /specif/i, d: board(
          rect(12, 3, 40, 38, 'pp', 1) + rect(17, 8, 18, 3, 'af') + text(17, 17, '1.1', 'tx num') + rows(26, 15.5, 3, 20, 4) + text(17, 31, '1.2', 'tx num') + rows(26, 29.5, 2, 20, 4) + rect(56, 8, 14, 18, 'pp2', 1)) +
        prop(shadow(118, 101, 24) + rect(102, 72, 7, 29, 'bk1', 1) + rect(110, 76, 7, 25, 'bk2', 1) + rect(118, 70, 7, 31, 'bk3', 1) + circ(138, 86, 7, 'mag') + line(143, 91, 150, 98, 'magh')) },
      // Fabrication
      { k: 'shop', re: /shop drawing|lod 400/i, d: board(
          rect(18, 8, 34, 26, 'plate', 1) + [[24, 14], [46, 14], [24, 28], [46, 28]].map(function (p) { return circ(p[0], p[1], 2.4, 'bolt'); }).join('') + line(18, 39, 52, 39, 'dim') + line(18, 37, 18, 41, 'dim') + line(52, 37, 52, 41, 'dim') + text(30, 38, '200', 'tx num') + line(58, 8, 58, 34, 'dim')) +
        prop(shadow(120, 101, 26) + box(120, 101, 8, 28, 8, 'm') + poly([[112, 97], [112, 77], [126, 70], [126, 90]], 'plateP') + circ(116, 81, 1.4, 'boltP') + circ(122, 78, 1.4, 'boltP') + circ(116, 89, 1.4, 'boltP') + circ(122, 86, 1.4, 'boltP')) },
      { k: 'prefab', re: /prefab|modular/i, d: board(
          [0, 1, 2].map(function (c) { return [0, 1].map(function (r) { return rect(8 + c * 21, 6 + r * 17, 18, 14, c === 1 && r === 0 ? 'modOn' : 'mod', 1); }).join(''); }).join('')) +
        prop(shadow(120, 101, 28) + box(112, 101, 12, 16, 10, 'p') + box(112, 91, 12, 16, 10, 'a') + line(138, 50, 138, 66, 'cord') + path('M136 66h4v3h-4z', 'hook') + box(138, 84, 10, 12, 10, 'p')) },
      { k: 'cnc', re: /cnc|machine/i, d: board(
          rect(3, 3, 70, 38, 'term', 1) + text(7, 12, 'G01 X12 Y40', 'tx code') + text(7, 20, 'G02 X30 I9', 'tx code') + text(7, 28, 'M03 S1200', 'tx code') + rect(7, 32, 5, 4, 'af')) +
        prop(shadow(120, 101, 32) + box(120, 101, 26, 26, 6, 'm') + poly([[120, 93], [104, 85], [120, 77], [136, 85]], 'sheet') + path('M110 85l10-5 10 5', 'cut') + line(104, 80, 104, 62, 'gan') + line(136, 80, 136, 62, 'gan') + line(104, 62, 136, 62, 'gan') + rect(117, 62, 6, 12, 'spin') + circ(120, 80, 2.2, 'spark')) },
      // 4D / 5D
      { k: 'd4', re: /4d|schedul/i, d: board(
          [[6, 18], [14, 22], [24, 26], [36, 20], [48, 18]].map(function (b, i) { return rect(b[0], 6 + i * 7, b[1], 4.5, i % 2 ? 'gy' : 'ga', 1); }).join('') + line(40, 3, 40, 41, 'today')) +
        prop(shadow(120, 101, 22) + box(120, 101, 16, 16, 14, 'b') + wire(120, 87, 16, 16, 18) + circ(146, 72, 8, 'clock') + line(146, 72, 146, 67, 'hand') + line(146, 72, 150, 72, 'hand')) },
      { k: 'd5', re: /5d|cost plan/i, d: board(
          '<circle class="donut d1" cx="22" cy="22" r="11"/><circle class="donut d2" cx="22" cy="22" r="11"/><circle class="donut d3" cx="22" cy="22" r="11"/>' +
          rect(42, 12, 5, 4, 'ga') + line(50, 14, 68, 14, 'lw') + rect(42, 20, 5, 4, 'gy') + line(50, 22, 64, 22, 'lw') + rect(42, 28, 5, 4, 'gr2') + line(50, 30, 66, 30, 'lw')) +
        prop(shadow(120, 101, 26) + box(108, 101, 13, 13, 22, 'b') + coins(134, 101, 4) + coins(146, 98, 2)) },
      { k: 'cash', re: /cash|forecast/i, d: board(
          line(6, 38, 70, 38, 'lf') + line(6, 4, 6, 38, 'lf') + path('M6 37C26 37 30 8 70 6V38H6z', 'area') + path('M6 37C26 37 30 8 70 6', 'ln2') + circ(46, 12, 2, 'dot')) +
        prop(shadow(120, 101, 26) + coins(104, 101, 2) + coins(120, 101, 4) + coins(136, 101, 6)) },
      // Construction
      { k: 'hse', re: /logistic|\bhse\b|safety/i, d: board(
          rect(6, 5, 64, 34, 'site') + circ(28, 22, 13, 'radius') + rect(25, 19, 6, 6, 'af') + rect(46, 10, 16, 10, 'sk') + path('M50 34h18', 'ln') + path('M64 31l4 3-4 3', 'ln')) +
        prop(shadow(120, 101, 32) + poly([[98, 100], [104, 82], [110, 100]], 'cone') + rect(100.5, 90, 7, 2.5, 'band') + poly([[136, 101], [141, 86], [146, 101]], 'cone') + rect(138, 93, 6, 2.5, 'band') +
          rect(112, 88, 22, 4, 'barrier') + line(115, 92, 115, 101, 'leg') + line(131, 92, 131, 101, 'leg') + path('M114 76a9 7 0 0 1 18 0h3v2h-24v-2z', 'hat')) },
      { k: 'seq', re: /sequenc|phas/i, d: board(
          [0, 1, 2].map(function (i) { return circ(14 + i * 24, 22, 7, i === 2 ? 'stepOn' : 'step') + text(11.8 + i * 24, 25.5, String(i + 1), 'tx ' + (i === 2 ? 'dark' : '')); }).join('') + path('M22 22h8M46 22h8', 'ln')) +
        prop(shadow(120, 101, 32) + box(98, 101, 9, 9, 8, 'p') + box(120, 101, 9, 9, 18, 'p') + box(142, 101, 9, 9, 30, 'a')) },
      { k: 'prog', re: /progress|track/i, d: board(
          '<circle class="ringBg" cx="20" cy="22" r="11"/><circle class="ring" cx="20" cy="22" r="11"/>' + text(14.5, 24.5, '68%', 'tx num') + rect(40, 12, 28, 4, 'barBg', 2) + rect(40, 12, 22, 4, 'ga', 2) + rect(40, 22, 28, 4, 'barBg', 2) + rect(40, 22, 14, 4, 'gy', 2) + rect(40, 32, 28, 4, 'barBg', 2) + rect(40, 32, 8, 4, 'gr2', 2)) +
        prop(shadow(120, 101, 24) + box(120, 101, 18, 18, 22, 'b') + wire(120, 79, 18, 18, 14) + [0, 1].map(function (i) { return line(102, 70 - i * 7, 120, 79 - i * 7, 'scaf') + line(120, 79 - i * 7, 138, 70 - i * 7, 'scaf'); }).join('')) },
      // Operation & maintenance
      { k: 'twin', re: /twin/i, d: board(
          wire(38, 40, 14, 14, 20).replace(/class="w/g, 'class="tw') + path('M58 10a8 8 0 0 1 8 8M58 4a14 14 0 0 1 14 14', 'sig')) +
        prop(shadow(120, 101, 22) + box(120, 101, 15, 15, 26, 'b') + path('M142 80c6-6 6-14 0-20', 'syn') + path('M139 62l3-3 3 3', 'syn')) },
      { k: 'cobie', re: /cobie|asset/i, d: board(
          [0, 1, 2].map(function (i) { return rect(6, 6 + i * 12, 8, 8, 'qr') + rect(8, 8 + i * 12, 2, 2, 'qrd') + rect(11, 11 + i * 12, 2, 2, 'qrd') + line(18, 9 + i * 12, 54, 9 + i * 12, 'lw') + line(18, 12.5 + i * 12, 40, 12.5 + i * 12, 'lf'); }).join('')) +
        prop(shadow(120, 101, 28) + box(120, 101, 22, 14, 18, 'm') + ell(108, 82, 5, 6, 'fan') + line(108, 77, 108, 87, 'fanb') + line(128, 76, 132, 86, 'cord') + rect(128, 86, 9, 7, 'tag', 1)) },
      { k: 'cafm', re: /cafm|\bfm\b|facilit/i, d: board(
          path('M8 30a14 14 0 0 1 28 0', 'gaugeBg') + path('M8 30a14 14 0 0 1 22-11.5', 'gauge') + line(22, 30, 30, 21, 'hand') + [8, 14, 6, 11].map(function (h, i) { return rect(44 + i * 7, 32 - h, 5, h, 'ga', 1); }).join('') + circ(66, 8, 2.4, 'okDot') + circ(59, 8, 2.4, 'warnDot')) +
        prop(shadow(120, 101, 24) + box(120, 101, 20, 12, 12, 'r') + path('M108 80v-4h12v4', 'handle') + line(140, 98, 152, 86, 'wrench') + circ(153, 85, 3, 'wrenchH')) },
      // Renovation
      { k: 'scan', re: /scan|as-built/i, d: board(
          (function () { var s = '', seed = 5; for (var i = 0; i < 70; i++) { seed = (seed * 9301 + 49297) % 233280; var r = seed / 233280; seed = (seed * 9301 + 49297) % 233280; var q = seed / 233280; var x = 14 + r * 48, y = 6 + q * 32; if (x > 30 && x < 46 && y < 16) continue; s += circ(x, y, 0.9, 'pt'); } return s; })() + path('M14 38V18l16-12 16 12v20', 'ptl')) +
        prop(poly([[120, 72], [84, 46], [84, 62]], 'beam') + poly([[120, 72], [158, 40], [158, 58]], 'beam') + shadow(120, 101, 14) + line(120, 84, 110, 101, 'leg') + line(120, 84, 130, 101, 'leg') + line(120, 84, 120, 101, 'leg') + rect(114, 66, 12, 18, 'scan', 3) + circ(120, 72, 3, 'lens')) },
      { k: 'hbim', re: /heritage|hbim|refurb/i, d: board(
          rect(4, 30, 68, 10, 'stone') + [0, 1, 2, 3].map(function (i) { return path('M' + (8 + i * 16) + ' 30v-12a6 6 0 0 1 12 0v12', 'archP'); }).join('') + path('M4 10h68', 'lw') + (function () { var s = ''; for (var i = 0; i < 9; i++) s += path('M' + (8 + i * 7.5) + ' 4l3 3-3 3-3-3z', 'lat'); return s; })()) +
        prop(shadow(120, 101, 18) + box(120, 102, 12, 12, 4, 'p') + cyl(120, 96, 6, 30, 'p') + box(120, 70, 10, 10, 3, 'p') + path('M114 92v-24M117 93v-24M123 93v-24M126 92v-24', 'flute')) },
      { k: 'retro', re: /retrofit|impact/i, d: board(
          rect(10, 10, 9, 28, 'barGrey', 1) + rect(21, 22, 9, 16, 'barG', 1) + rect(42, 14, 9, 24, 'barGrey', 1) + rect(53, 26, 9, 12, 'barG', 1) + path('M32 12v8m-3-3 3 3 3-3', 'ln')) +
        prop(shadow(120, 101, 22) + box(120, 101, 16, 16, 26, 'b') + poly([[122, 99], [136, 92], [136, 70], [122, 77]], 'skin') + path('M146 80v-12m-4 4 4-4 4 4', 'up')) },
      // Demolition
      { k: 'decon', re: /deconstruct|selective/i, d: board(
          rect(8, 6, 60, 32, 'plan') + line(8, 20, 68, 20, 'cutl') + line(36, 6, 36, 38, 'cutl') + [['1', 18, 15], ['2', 48, 15], ['3', 18, 31], ['4', 48, 31]].map(function (n) { return circ(n[1] + 2, n[2] - 2.5, 4.4, 'step') + text(n[1], n[2], n[0], 'tx sm'); }).join('')) +
        prop(shadow(120, 101, 24) + box(120, 101, 16, 16, 12, 'b') + box(124, 82, 14, 14, 10, 'b') + box(128, 64, 11, 11, 8, 'a') + path('M141 76v-6m-3 3 3-3 3 3', 'up')) },
      { k: 'recov', re: /material|waste|recover/i, d: board(
          [['g', 'Steel', 12], ['a', 'Concrete', 24], ['y', 'Glass', 36]].map(function (r, i) { return circ(10, 8 + i * 12, 3, 'dot' + r[0]) + line(17, 8 + i * 12, 46, 8 + i * 12, 'lw') + text(56, 10 + i * 12, String(r[2] * 3), 'tx num'); }).join('')) +
        prop(shadow(120, 101, 32) + box(100, 101, 9, 9, 13, 'g') + box(120, 101, 9, 9, 13, 'a') + box(140, 101, 9, 9, 13, 'y') + path('M115 70a6 6 0 1 1 6 6m0 0 3-3m-3 3 3 3', 'recy')) },
      { k: 'restore', re: /restor|sgi|site/i, d: board(
          rect(10, 4, 34, 36, 'pp', 1) + rows(15, 11, 3, 22, 4.5) + path('M15 34l7-5 6 3 10-9', 'lnG') + path('M56 32c0-12 8-18 14-18 0 12-6 18-14 18z', 'leaf') + line(56, 32, 66, 20, 'leafv')) +
        prop(shadow(120, 103, 34) + box(120, 103, 30, 30, 3, 'g') + line(106, 90, 106, 80, 'trunk') + circ(106, 76, 6, 'g1') + line(132, 92, 132, 78, 'trunk') + circ(132, 73, 8, 'g2') + path('M120 96v-6m0 2c-3 0-5-2-5-4 3 0 5 1 5 4m0-1c3 0 5-2 5-4-3 0-5 1-5 4', 'sprout')) }
    ];
    return function (stage, k, name) {
      var s = null;
      for (var i = 0; i < scenes.length && !s; i++) if (scenes[i].re.test(name)) s = scenes[i];
      if (!s) s = scenes[(stage * 3 + k) % scenes.length];
      return room + '<g class="sc-' + s.k + '">' + s.d + '</g>';
    };
  })();
  function fillRoom(i, landing) {
    var stage = stages[i];
    if (!stage || !insideEl) return;
    insideEl.querySelector('[data-dz-room-num]').textContent = pad(i + 1);
    insideEl.querySelector('[data-dz-room-meta]').textContent = servicesText.replace('[count]', stage.services.length);
    insideEl.querySelector('[data-dz-room-title]').textContent = stage.title;
    floorsEl.classList.remove('is-on');
    floorsEl.classList.toggle('is-landing', !!landing);
    floorsEl.innerHTML = '';
    stage.services.forEach(function (svc, k) {
      var floor = floorTpl.content.firstElementChild.cloneNode(true);
      floor.style.setProperty('--i', k);
      floor.querySelector('.dz-floor__lot').textContent = letters[k] || '';
      floor.querySelector('.dz-floor__name').textContent = svc.name;
      floor.querySelector('.dz-floor__art').innerHTML = art(i, k, svc.name);
      var go = floor.querySelector('.dz-floor__go');
      if (svc.url) go.setAttribute('href', svc.url);
      else go.remove();
      floor.addEventListener('pointerenter', function () { liftEl.style.setProperty('--lift', k); });
      floor.addEventListener('focusin', function () { liftEl.style.setProperty('--lift', k); });
      floorsEl.appendChild(floor);
    });
    liftEl.style.setProperty('--n', stage.services.length || 1);
    liftEl.style.setProperty('--lift', 0);
    if (dotsEl) {
      dotsEl.innerHTML = '';
      stages.forEach(function (s, k) { var d = document.createElement('span'); if (k === i) d.className = 'is-on'; dotsEl.appendChild(d); });
    }
    if (!landing) land();
  }
  // Floors appear, then (a frame later, so their transitions run) their lights come on.
  function land() {
    floorsEl.classList.remove('is-landing');
    requestAnimationFrame(function () { requestAnimationFrame(function () { floorsEl.classList.add('is-on'); }); });
  }
  function enter(i, dir, byKeyboard) {
    if (!insideEl || !stages[i] || i === inside) return;
    markVisited(i);
    if (inside > -1) {
      // Already inside: move to the other building, sliding the way of travel.
      inside = i;
      if (current !== i) show(i);
      light(i);
      fillRoom(i, false);
      insideEl.classList.remove('is-sliding-next', 'is-sliding-prev');
      requestAnimationFrame(function () { insideEl.classList.add(dir < 0 ? 'is-sliding-prev' : 'is-sliding-next'); });
      if (selected > -1) { selected = i; paintSelection(); }
      return;
    }
    inside = i;
    setHot(null);
    stop();
    driftTo(0, 0);
    if (current !== i) show(i);
    light(i);
    if (card) closeCard();
    var bEl = buildingEl(i);
    var bb = buildingBounds[i];
    if (bb && view && !reduce) {
      var cw = mapSize.w, ch = mapSize.h;
      var w = Math.max(bb.maxX - bb.minX, (bb.maxY - bb.minY) * cw / ch) * 1.3;
      var h = w * ch / cw;
      diveInto({ x: (bb.minX + bb.maxX) / 2 - w / 2, y: (bb.minY + bb.maxY) / 2 - h / 2, w: w, h: h }, 600);
    }
    map.classList.add('is-entering');
    // While the camera dives (a transform, so the page is otherwise idle) the interior is built
    // and drawn, invisible; opening it then only switches it on.
    var slide = !reduce && bEl && Element.prototype.animate;
    fillRoom(i, slide);
    insideEl.hidden = false;
    clearTimeout(openTimer);
    openTimer = setTimeout(function () { if (inside === i) openInterior(i, bEl, slide, byKeyboard); }, reduce ? 0 : 620);
  }
  function openInterior(i, bEl, slide, byKeyboard) {
    // Reads first (one layout, already up to date: the interior was built during the dive)...
    var from = bEl ? bEl.getBoundingClientRect() : null;
    var floors = Array.prototype.slice.call(floorsEl.children);
    var targets = floors.map(function (f) { return f.getBoundingClientRect(); });
    // ...then writes.
    insideEl.scrollTop = 0;
    if (compact()) {
      // The browser's own pushState: the theme wraps history.pushState to save and later restore
      // the page's scroll position, which this in-page step does not need (and which costs a
      // layout on the way back).
      try {
        // Coming back to this entry, the theme would restore a scroll position it saved earlier
        // (possibly stale, always a forced layout). The page did not scroll, so drop it; the theme
        // saves a fresh one whenever the page is actually left.
        var state = history.state && typeof history.state === 'object' ? Object.assign({}, history.state) : null;
        if (state && 'scrollTop' in state) { delete state.scrollTop; History.prototype.replaceState.call(history, state, ''); }
        History.prototype.pushState.call(history, { dzInside: 'home' }, '');
        pushed = true;
      } catch (e) {}
    }
    slide = slide && from && from.width;
    if (!slide) land();
    map.classList.add('is-inside');
    insideEl.classList.add('is-open');
    // Focus moves into the interior: at once for keyboard users, after the entrance has played
    // for pointer and touch users (moving focus updates the accessibility tree for the new
    // content, which would cost a frame in the middle of the animation).
    var title = insideEl.querySelector('[data-dz-room-title]');
    if (title) {
      if (byKeyboard) requestAnimationFrame(function () { title.focus({ preventScroll: true }); });
      else setTimeout(function () { if (inside === i && document.activeElement !== title && !insideEl.contains(document.activeElement)) title.focus({ preventScroll: true }); }, reduce ? 0 : 1500);
    }
    if (!slide) return;
    // The building's slices, top floor first from the top, fly to their floors.
    var n = floors.length;
    var slabs = [];
    var lastAnim = null;
    floors.forEach(function (f, k) {
      var to = targets[k];
      var sliceH = from.height / n;
      var top = from.top + (n - 1 - k) * sliceH;
      var s = document.createElement('div');
      s.className = 'dz-slab-home';
      s.style.left = to.left + 'px';
      s.style.top = to.top + 'px';
      s.style.width = to.width + 'px';
      s.style.height = to.height + 'px';
      document.body.appendChild(s);
      slabs.push(s);
      lastAnim = s.animate([
        { transform: 'translate(' + (from.left - to.left) + 'px, ' + (top - to.top) + 'px) scale(' + (from.width / to.width) + ', ' + (sliceH / to.height) + ')' },
        { transform: 'none' }
      ], { duration: 640, delay: (n - 1 - k) * 90, easing: 'cubic-bezier(0.2, 0.9, 0.2, 1)', fill: 'both' });
    });
    lastAnim.onfinish = function () {
      land();
      slabs.forEach(function (s) {
        var fade = s.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 280, fill: 'forwards' });
        fade.onfinish = function () { s.remove(); };
      });
    };
  }
  // Focus goes back to the building only for keyboard users (it would also raise the building).
  function leave(byKeyboard, fromHistory) {
    if (inside < 0) return;
    if (pushed && !fromHistory) {
      // The entry added a history step (phones): going back runs this again from popstate. If the
      // browser does not report it, leave anyway.
      history.back();
      var stuck = inside;
      setTimeout(function () { if (inside === stuck && pushed) { pushed = false; leave(byKeyboard, true); } }, 500);
      return;
    }
    pushed = false;
    var was = inside;
    inside = -1;
    clearTimeout(openTimer);
    insideEl.classList.remove('is-open', 'is-sliding-next', 'is-sliding-prev');
    map.classList.remove('is-inside');
    diveOut(reduce ? 0 : 520);
    openTimer = setTimeout(function () {
      if (inside > -1) return;
      insideEl.hidden = true;
      if (compact()) {
        // Back on the city, focused on the building just visited, card ready.
        map.classList.remove('is-entering');
        select(was);
      } else {
        map.classList.remove('is-entering');
        if (selected > -1) deselect(true);
        frame();
        var b = buildingEl(was);
        if (b && byKeyboard) b.focus({ preventScroll: true });
        schedule();
      }
    }, reduce ? 0 : 540);
  }
  function hop(dir) {
    var n = stages.length;
    enter((inside + dir + n) % n, dir);
  }
  if (insideEl) {
    // A click with detail 0 came from the keyboard (Enter or Space on the button).
    insideEl.querySelector('[data-dz-back]').addEventListener('click', function (e) { leave(e.detail === 0); });
    insideEl.querySelectorAll('[data-dz-hop]').forEach(function (btn) {
      btn.addEventListener('click', function () { hop(parseInt(btn.getAttribute('data-dz-hop'), 10)); });
    });
    // The interior may sit outside the section on phones, so it listens for Esc itself.
    insideEl.addEventListener('keydown', function (e) { if (e.key === 'Escape') leave(true); });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (inside > -1) leave(true);
      else if (selected > -1 && !(insideEl && insideEl.contains(e.target))) deselect();
    });
    insideEl.addEventListener('touchmove', function (e) {
      if (insideEl.classList.contains('is-full') && insideEl.scrollHeight <= insideEl.clientHeight) e.preventDefault();
    }, { passive: false });
    // Swipe sideways to move between buildings.
    var sx = 0, sy = 0;
    insideEl.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
    insideEl.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) hop(dx < 0 ? 1 : -1);
    }, { passive: true });
    window.addEventListener('popstate', function () { if (inside > -1 && pushed) leave(false, true); });
  }

  // Touch and mouse layouts switch with the screen width.
  function placeInterior(c) {
    if (!insideEl) return;
    insideEl.classList.toggle('is-full', c);
    var home = c ? document.body : map;
    if (insideEl.parentNode !== home) home.appendChild(insideEl);
  }
  function applyMode() {
    var c = compact();
    if (inside > -1) leave(false, true);
    placeInterior(c);
    if (cam) cam.classList.toggle('is-compact', c);
    if (hintEl) hintEl.textContent = (c ? hintEl.dataset.touch : hintEl.dataset.mouse) || '';
    frame();
  }
  if (compactMq.addEventListener) compactMq.addEventListener('change', applyMode);

  // ---------- The view ----------
  // Desktop: the whole city when it is readable (at least as big as drawn), otherwise the current
  // plot. Touch screens: the whole city (room left for the hint), or the selected plot framed
  // above its card. Inside a building the camera stays put.
  var view = null;
  // A selected building is framed with its name sign, raised and floating above it, in view.
  function selBounds(i) {
    var b = blockBounds[i], l = labels[i];
    if (!l) return b;
    var signTop = l.anchor[1] - (l.lift || 0) - 11 - 14 - 38;
    return { minX: Math.min(b.minX, l.anchor[0] - 40), maxX: Math.max(b.maxX, l.anchor[0] + 40), minY: Math.min(b.minY, signTop), maxY: b.maxY };
  }
  function frame() {
    if (!svg || !full || !map || inside > -1) return;
    var cw = mapSize.w;
    var ch = mapSize.h;
    if (!cw || !ch) return;
    var target, w, h, bb, zoom, cx, cy;
    if (compact()) {
      map.classList.add('is-panning');
      if (selected > -1 && blockBounds[selected]) {
        bb = selBounds(selected);
        var room = ch - (cardH ? cardH + 24 : 0);
        zoom = Math.max(0.9, Math.min(1.7, Math.min(cw / ((bb.maxX - bb.minX) * 1.5), room / ((bb.maxY - bb.minY) * 1.3))));
        w = cw / zoom; h = ch / zoom;
        cx = (bb.minX + bb.maxX) / 2; cy = (bb.minY + bb.maxY) / 2;
        target = { x: cx - w / 2, y: cy - room / 2 / zoom, w: w, h: h };
      } else {
        var usable = ch - 48;
        zoom = Math.min(cw / full.w, usable / full.h);
        w = cw / zoom; h = ch / zoom;
        target = { x: full.x - (w - full.w) / 2, y: full.y - (usable / zoom - full.h) / 2, w: w, h: h };
      }
      glide(target);
      return;
    }
    if (selected > -1 && blockBounds[selected]) {
      // The selected building, close up, centred above its card.
      bb = selBounds(selected);
      var free = ch - (cardH ? cardH + 32 : 0);
      zoom = Math.max(1.2, Math.min(2.2, Math.min(cw / ((bb.maxX - bb.minX) * 1.6), free / ((bb.maxY - bb.minY) * 1.3))));
      w = cw / zoom; h = ch / zoom;
      cx = (bb.minX + bb.maxX) / 2; cy = (bb.minY + bb.maxY) / 2;
      target = { x: cx - w / 2, y: cy - free / 2 / zoom, w: w, h: h };
      map.classList.add('is-panning');
      glide(target, 900);
      return;
    }
    var fit = Math.min(cw / full.w, ch / full.h);
    if (fit >= 1) {
      w = cw / fit; h = ch / fit;
      target = { x: full.x - (w - full.w) / 2, y: full.y - (h - full.h) / 2, w: w, h: h };
      map.classList.remove('is-panning');
    } else {
      bb = blockBounds[current] || { minX: full.x, maxX: full.x + full.w, minY: full.y, maxY: full.y + full.h };
      zoom = Math.max(1.1, Math.min(1.5, Math.min(cw / ((bb.maxX - bb.minX) * 1.35), ch / ((bb.maxY - bb.minY) * 1.25))));
      w = cw / zoom; h = ch / zoom;
      cx = (bb.minX + bb.maxX) / 2; cy = (bb.minY + bb.maxY) / 2;
      target = { x: cx - w / 2, y: cy - h / 2, w: w, h: h };
      map.classList.add('is-panning');
    }
    glide(target);
  }
  function setBox(v) {
    var box = v.x.toFixed(1) + ' ' + v.y.toFixed(1) + ' ' + v.w.toFixed(1) + ' ' + v.h.toFixed(1);
    svg.setAttribute('viewBox', box);
    trafficSvg.setAttribute('viewBox', box);
    topSvg.setAttribute('viewBox', box);
  }
  // A camera move jumps the SVGs to the destination once (one redraw), then plays the move as a
  // transform on the camera layer, from "looks like where we were" to rest. The browser moves the
  // drawn city on the graphics card instead of redrawing it on every frame.
  var camAnim = null;
  // The dive into a building zooms the camera layer only: the city is not redrawn at the
  // building's scale, and coming back (diveOut) returns to exactly the view that was left.
  var dive = null;
  function diveInto(target, duration) {
    settleCamera();
    var a = view.w / target.w;
    var sT = mapSize.w / target.w;
    var m = 'matrix(' + a + ', 0, 0, ' + a + ', ' + (sT * (view.x - target.x)) + ', ' + (sT * (view.y - target.y)) + ')';
    dive = cam.animate([{ transform: 'none' }, { transform: m }], { duration: duration, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', fill: 'forwards' });
    dive.matrix = m;
  }
  function diveOut(duration) {
    if (!dive) return;
    var m = dive.matrix;
    dive.cancel();
    dive = null;
    if (!duration) return;
    var back = cam.animate([{ transform: m }, { transform: 'none' }], { duration: duration, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
    camAnim = back;
    back.onfinish = function () { if (camAnim === back) camAnim = null; };
  }
  // Where the camera visibly is right now, including a move or drag in progress.
  function shownView() {
    if (!view) return view;
    var a = 1, e = offset[0], f = offset[1];
    if (camAnim || dive) {
      var m = new DOMMatrixReadOnly(getComputedStyle(cam).transform);
      a = m.a; e = m.e; f = m.f;
    }
    var s = mapSize.w / view.w;
    return { x: view.x - e / (a * s), y: view.y - f / (a * s), w: view.w / a, h: view.h / a };
  }
  function settleCamera() {
    if (coastFrame) { cancelAnimationFrame(coastFrame); coastFrame = 0; }
    if (camAnim || dive || offset[0] || offset[1]) {
      var now = shownView();
      if (camAnim) { camAnim.cancel(); camAnim = null; }
      if (dive) { dive.cancel(); dive = null; }
      offset = [0, 0];
      cam.style.transform = '';
      view = now;
      setBox(view);
    }
  }
  function glide(target, duration) {
    var from = shownView();
    settleCamera();
    view = target;
    setBox(view);
    if (!from || reduce || !cam.animate) return;
    var sF = mapSize.w / from.w;
    var a0 = target.w / from.w, e0 = sF * (target.x - from.x), f0 = sF * (target.y - from.y);
    if (Math.abs(a0 - 1) < 0.001 && Math.abs(e0) < 0.5 && Math.abs(f0) < 0.5) return;
    camAnim = cam.animate([
      { transform: 'matrix(' + a0 + ', 0, 0, ' + a0 + ', ' + e0 + ', ' + f0 + ')' },
      { transform: 'none' }
    ], { duration: duration || 800, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
    var mine = camAnim;
    camAnim.onfinish = function () { if (camAnim === mine) camAnim = null; };
  }
  // The map's and the card's sizes, reported by the browser after its own layout.
  var mapSize = { w: 0, h: 0 };
  var cardH = 0;
  function measure() { if (map) mapSize = { w: map.clientWidth, h: map.clientHeight }; if (card) cardH = card.offsetHeight; }
  if ('ResizeObserver' in window && map) {
    new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        var box = en.borderBoxSize && en.borderBoxSize[0];
        if (en.target === map) {
          mapSize = { w: en.contentRect.width, h: en.contentRect.height };
          frame();
        } else {
          cardH = box ? box.blockSize : en.target.offsetHeight;
        }
      });
    }).observe(map);
    if (card) cardObserver();
  }
  function cardObserver() {
    new ResizeObserver(function (entries) {
      var box = entries[0].borderBoxSize && entries[0].borderBoxSize[0];
      cardH = box ? box.blockSize : card.offsetHeight;
    }).observe(card);
  }

  function stop() {
    if (timer) clearTimeout(timer);
    timer = null;
  }
  function schedule() {
    stop();
    if (paused || held || !visible || inside > -1 || selected > -1) return;
    started = Date.now();
    timer = setTimeout(function () { show(current + 1); }, remaining);
  }
  function used() {
    if (timer) remaining = Math.max(0, remaining - (Date.now() - started));
  }
  // Hover or focus on the services holds the timer so the stage does not change under the pointer.
  function hold(on) {
    if (held === on) return;
    if (on) used();
    held = on;
    root.classList.toggle('dz-off', held || !visible);
    schedule();
  }
  function setPaused(on) {
    if (on) used();
    paused = on;
    root.classList.toggle('is-paused', on);
    if (playBtn) {
      playBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      playBtn.setAttribute('aria-label', on ? playBtn.dataset.play : playBtn.dataset.pause);
    }
    schedule();
  }

  steps.forEach(function (step, i) {
    step.addEventListener('click', function () { show(i, true); });
  });
  var prev = root.querySelector('.dz__ctl--prev');
  var next = root.querySelector('.dz__ctl--next');
  var restart = root.querySelector('.dz__ctl--restart');
  if (prev) prev.addEventListener('click', function () { show(current - 1, true); });
  if (next) next.addEventListener('click', function () { show(current + 1, true); });
  if (restart) restart.addEventListener('click', function () {
    if (paused && !reduce) setPaused(false);
    show(0, true);
  });
  if (playBtn) playBtn.addEventListener('click', function () { setPaused(!paused); });

  // Hover or focus on the map holds the timer.
  [map].forEach(function (area) {
    if (!area) return;
    area.addEventListener('pointerenter', function () { hold(true); });
    area.addEventListener('pointerleave', function () { hold(false); });
    area.addEventListener('focusin', function () { hold(true); });
    area.addEventListener('focusout', function (e) { if (!area.contains(e.relatedTarget)) hold(false); });
  });

  // Only play while the section is on screen.
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      var now = entries[0].isIntersecting;
      if (now) arrive();
      if (trafficSvg && trafficSvg.pauseAnimations) { if (now) trafficSvg.unpauseAnimations(); else trafficSvg.pauseAnimations(); }
      if (now === visible) return;
      if (!now) used();
      visible = now;
      root.classList.toggle('dz-off', held || !visible);
      schedule();
    }, { threshold: 0.25 }).observe(root);
  }

  draw();
  light(current);
  paintVisited();
  measure();
  applyMode();
  if (!('IntersectionObserver' in window)) arrive();
  else setTimeout(arriveIfShown, 60);
  setPaused(paused);
}

function BimJourney() {
  const ref = useBjRef(null);
  useBjLayoutEffect(() => {
    const host = ref.current;
    if (!host || host.firstChild) return;
    const hero = (window.DZ_HOME && window.DZ_HOME.hero) || {};
    host.innerHTML = bjMarkup(hero, bjStages());
    bjInit(host.firstChild);
  }, []);
  return <section className="dz-bim-journey" id="dz-main" aria-labelledby="hero-title" ref={ref} />;
}

window.BimJourney = BimJourney;
